import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import axios from 'axios';

import { BannerDetails } from '../BannerDetails/BannerDetails';
import { Loading, Modal, Section } from '../../shared';
import { LanguageType } from '@lifeserver/shared';

import { useBanner, useUpdateBanner } from '../../hooks/banners';

import { handleError } from '../../utils/utils';

import { RouteTo } from '../../routes';

import { CreateOrUpdateBanner } from '../../types/bannerTypes';

type BannerProps = { language: LanguageType };

export const BannerUpdatePage = (props: BannerProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const bannerId = params.id as string;

    const {
        banner: fetchedBanner,
        fetch: fetchBanner,
        errorMessage: fetchBannerError,
        isLoading: isFetchBannerLoading,
    } = useBanner(bannerId);
    const {
        updatedBanner,
        isLoading: isUpdateBannerLoading,
        updateBanner,
    } = useUpdateBanner(bannerId);

    useEffect(() => {
        fetchBanner();
    }, []);

    async function onSave(banner: CreateOrUpdateBanner) {
        try {
            await updateBanner(banner);
            return true;
        } catch {
            return false;
        }
    }

    const deleteBanner = async () => {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete banner',
            text: 'Really delete this banner?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await axios.delete('/api/banners/v2/' + bannerId);

            navigate(RouteTo.Banners());
        } catch (error) {
            handleError('Error deleting banner', error);
        }
    };

    if (isFetchBannerLoading) {
        return <Loading />;
    }

    if (fetchBannerError || !fetchedBanner) {
        return (
            <Section>{fetchBannerError ?? 'The banner does not exist'}</Section>
        );
    }

    return (
        <BannerDetails
            language={props.language}
            isNew={false}
            onSave={onSave}
            isSaving={isUpdateBannerLoading}
            initialBanner={updatedBanner ?? fetchedBanner}
            onDelete={deleteBanner}
        />
    );
};
